import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';

const MinecraftModsSite = () => {
  const [currentLang, setCurrentLang] = useState('tr');
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [user, setUser] = useState(null);
  const mountRef = useRef(null);
  const sceneRef = useRef(null);

  // Çok dilli içerik
  const content = {
    tr: {
      title: 'Minecraft Modları',
      subtitle: 'Yüksek kaliteli modlar indir',
      description: 'Minecraft oyun deneyiminizi dönüştürün. En popüler ve yenilikçi Minecraft modlarını keşfedin. Kaos, Afet Sistemi, Doğa Modları ve daha fazlası. Minecraft 1.20+ için optimize edilmiş modlar.',
      download: 'İndir',
      login: 'Giriş Yap',
      register: 'Kayıt Ol',
      lang: 'English',
      seoTitle: 'Minecraft Modları İndir - Yüksek Kaliteli Mods - Tarsus Gece Dönercisi',
      seoDesc: 'Minecraft için premium modları indir. Kaosv2, Afet Sistemi, 3D animasyonlu modlar. Türkçe ve İngilizce desteği.',
      mods: [
        { id: 1, name: 'Kaosv2 (Yürü)', desc: 'Blok transformasyon sistemi' },
        { id: 2, name: 'Afet Sistemi', desc: '12 farklı doğal afet' },
        { id: 3, name: 'Başla Macerasına', desc: 'RPG öğeleri' }
      ],
      cookieMessage: 'Deneyimi iyileştirmek için çerezleri kabul edin',
      acceptCookies: 'Kabul Et',
      declineCookies: 'Reddet'
    },
    en: {
      title: 'Minecraft Mods',
      subtitle: 'Download premium modifications',
      description: 'Transform your Minecraft gaming experience. Discover the most popular and innovative Minecraft mods. Chaos system, Disaster events, Nature modifications and more. Optimized mods for Minecraft 1.20+.',
      download: 'Download',
      login: 'Login',
      register: 'Sign Up',
      lang: 'Türkçe',
      seoTitle: 'Minecraft Mods Download - High Quality Mods - Tarsus Night Doner',
      seoDesc: 'Download premium Minecraft mods. Kaosv2, Disaster System, 3D animated mods. Turkish and English support.',
      mods: [
        { id: 1, name: 'Kaosv2 (Chaos)', desc: 'Block transformation system' },
        { id: 2, name: 'Disaster System', desc: '12 different natural disasters' },
        { id: 3, name: 'Adventure Start', desc: 'RPG elements' }
      ],
      cookieMessage: 'Accept cookies to improve your experience',
      acceptCookies: 'Accept',
      declineCookies: 'Decline'
    }
  };

  const t = content[currentLang];

  // 3D Scene Kurulumu
  useEffect(() => {
    if (!mountRef.current) return;

    // WebGL desteklenmiyorsa (bazı cihazlar/tarayıcı ayarları) sessizce vazgeç,
    // uygulamanın tamamen çökmesini (siyah ekran) engelle.
    let renderer;
    try {
      const testCanvas = document.createElement('canvas');
      const gl = testCanvas.getContext('webgl') || testCanvas.getContext('experimental-webgl');
      if (!gl) {
        console.warn('WebGL desteklenmiyor, 3D arkaplan atlanıyor.');
        return;
      }
    } catch (e) {
      console.warn('WebGL kontrolü başarısız, 3D arkaplan atlanıyor.', e);
      return;
    }

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    let scene, camera, animationFrameId;

    try {
      scene = new THREE.Scene();
      scene.background = new THREE.Color(0x1a1a1a);
      sceneRef.current = scene;

      camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
      camera.position.z = 5;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      mountRef.current.appendChild(renderer.domElement);
    } catch (e) {
      console.warn('3D arkaplan başlatılamadı, atlanıyor.', e);
      return;
    }

    // Floating cubes
    const cubes = [];
    for (let i = 0; i < 8; i++) {
      const geometry = new THREE.BoxGeometry(0.8, 0.8, 0.8);
      const material = new THREE.MeshPhongMaterial({
        color: Math.random() > 0.5 ? 0xffffff : 0xff4444,
        shininess: 100
      });
      const cube = new THREE.Mesh(geometry, material);
      cube.position.set(
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 12,
        (Math.random() - 0.5) * 8
      );
      cube.velocity = {
        x: (Math.random() - 0.5) * 0.02,
        y: (Math.random() - 0.5) * 0.02,
        z: (Math.random() - 0.5) * 0.02
      };
      cube.rotation.set(Math.random(), Math.random(), Math.random());
      scene.add(cube);
      cubes.push(cube);
    }

    // Işık
    const light1 = new THREE.DirectionalLight(0xffffff, 0.8);
    light1.position.set(5, 5, 5);
    scene.add(light1);

    const light2 = new THREE.PointLight(0xff4444, 0.5);
    light2.position.set(-5, -5, 5);
    scene.add(light2);

    const ambientLight = new THREE.AmbientLight(0x666666);
    scene.add(ambientLight);

    // Animation loop
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      cubes.forEach(cube => {
        cube.position.add(cube.velocity);
        cube.rotation.x += 0.005;
        cube.rotation.y += 0.007;

        if (Math.abs(cube.position.x) > 8) cube.velocity.x *= -1;
        if (Math.abs(cube.position.y) > 8) cube.velocity.y *= -1;
        if (Math.abs(cube.position.z) > 6) cube.velocity.z *= -1;
      });

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const newWidth = mountRef.current?.clientWidth || width;
      const newHeight = mountRef.current?.clientHeight || height;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (renderer) {
        mountRef.current?.removeChild(renderer.domElement);
        renderer.dispose();
      }
    };
  }, []);

  return (
    <>
      {/* Meta tags for SEO */}
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="description" content={t.seoDesc} />
      <meta name="keywords" content="Minecraft mods, mod download, Kaosv2, Disaster System, Minecraft 1.20, gaming mods" />
      <meta name="author" content="Tarsus Gece Dönercisi Minecraft Team" />
      <meta property="og:title" content={t.seoTitle} />
      <meta property="og:description" content={t.seoDesc} />
      <meta property="og:type" content="website" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

      {/* Structured data for Google */}
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": t.title,
        "applicationCategory": "GameApplication",
        "description": t.description,
        "url": "https://minecraftmods.example.com",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "TRY"
        }
      })}</script>

      <div className="w-full min-h-screen bg-black text-white font-sans">
        {/* Header */}
        <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur border-b border-red-500/30">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="text-2xl font-bold tracking-wider" style={{ fontFamily: 'monospace' }}>
              ⬜ {currentLang === 'tr' ? 'MOD' : 'MODS'}
            </div>
            <nav className="flex items-center gap-6">
              <button
                onClick={() => setCurrentLang(currentLang === 'tr' ? 'en' : 'tr')}
                className="px-4 py-2 border border-gray-600 hover:border-red-500 transition-colors text-sm"
              >
                {t.lang}
              </button>
              <button
                onClick={() => {
                  setShowAuth(true);
                  setIsLogin(true);
                }}
                className="px-6 py-2 border border-gray-600 hover:border-white transition-colors"
              >
                {t.login}
              </button>
            </nav>
          </div>
        </header>

        {/* 3D Hero Section */}
        <section className="w-full h-screen relative overflow-hidden">
          <div ref={mountRef} className="w-full h-full absolute inset-0" />
          
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-gradient-to-b from-transparent via-transparent to-black/50">
            <h1 className="text-7xl font-bold text-white mb-4 text-center tracking-tighter drop-shadow-lg" 
                style={{ fontFamily: 'monospace', textShadow: '0 0 20px rgba(255, 68, 68, 0.5)' }}>
              {t.title}
            </h1>
            <p className="text-xl text-gray-300 mb-8 text-center max-w-2xl">{t.subtitle}</p>
            
            {/* Download Button with Bubble Animation */}
            <button
              onClick={() => alert('İndirme başlıyor...')}
              className="relative px-12 py-4 bg-white text-black font-bold text-lg overflow-hidden group"
              style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
            >
              {/* Red bubbles animation */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-4 h-4 bg-red-500 rounded-full opacity-70 animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      animation: `float ${2 + i * 0.3}s infinite ease-in-out`,
                      animationDelay: `${i * 0.2}s`
                    }}
                  />
                ))}
              </div>
              <span className="relative z-10">{t.download}</span>
            </button>

            {/* SEO Content Section */}
            <div className="mt-20 max-w-3xl text-center text-gray-400 text-sm leading-relaxed">
              <p className="mb-4">
                {t.description}
              </p>
              <div className="text-left space-y-2 text-xs">
                <p>🎮 <strong>Premium Minecraft Modifications</strong>: Oyun deneyiminizi yükseltmek için profesyonel olarak geliştirilmiş modlar. Tarsus Gece Dönercisi Minecraft Team tarafından sunulan en son ve en popüler modifikasyonları keşfedin.</p>
                <p>⚡ <strong>Optimized Performance</strong>: Tüm modlar Minecraft 1.20+ için optimize edilmiş ve test edilmiştir. Stabil performans ve maksimum FPS için tasarlandı.</p>
                <p>🔥 <strong>Kaosv2 Mod</strong>: Blok transformasyon sistemi ile sınırsız yaratıcılık. Dinamik çevre değişiklikleri ve yenilikçi gameplay mekaniklerini deneyimleyin.</p>
                <p>🌍 <strong>Afet Sistemi</strong>: 12 farklı doğal afeti Minecraft dünyasında yaşayın. Chunk-aware yüzey algılama ve gerçekçi fizik simülasyonu.</p>
                <p>🎨 <strong>Multiplayer Desteği</strong>: Sunucu ve yerel ağ oyunması için tam uyumluluk. Arkadaşlarınızla moda uyarlanmış düniyalarda oyun oynayın.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mods Grid */}
        <section className="bg-black py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 tracking-wider" style={{ color: '#ff4444' }}>
              {currentLang === 'tr' ? 'Popüler Modlar' : 'Popular Mods'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.mods.map(mod => (
                <div key={mod.id} className="border border-gray-700 hover:border-red-500 transition-colors p-6 group">
                  <div className="w-full h-32 bg-gradient-to-br from-gray-800 to-black mb-4 flex items-center justify-center border border-gray-700 group-hover:border-red-500/50">
                    <span className="text-gray-600 text-4xl">⬜</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{mod.name}</h3>
                  <p className="text-gray-400 mb-4">{mod.desc}</p>
                  <button className="w-full px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors font-bold">
                    {t.download}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cookie Notice */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 p-4 z-40">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
            <p className="text-gray-400">{t.cookieMessage}</p>
            <div className="flex gap-4">
              <button className="px-4 py-2 border border-gray-600 hover:border-white transition-colors text-xs">
                {t.declineCookies}
              </button>
              <button className="px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors text-xs">
                {t.acceptCookies}
              </button>
            </div>
          </div>
        </div>

        {/* Auth Modal */}
        {showAuth && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAuth(false)}>
            <div className="bg-gray-900 p-8 max-w-md w-full border border-gray-700" onClick={e => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-6">{isLogin ? t.login : t.register}</h2>
              
              {/* Google Sign-In Button */}
              <button 
                onClick={() => alert('Google Sign-In - Firebase entegrasyonu yapılacak')}
                className="w-full px-4 py-3 bg-white text-gray-900 hover:bg-gray-100 transition-colors font-bold mb-4 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {currentLang === 'tr' ? 'Google ile Giriş Yap' : 'Sign in with Google'}
              </button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900 text-gray-500">
                    {currentLang === 'tr' ? 'veya' : 'or'}
                  </span>
                </div>
              </div>

              {/* Email/Password Form */}
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500"
                />
                <input
                  type="password"
                  placeholder={currentLang === 'tr' ? 'Şifre' : 'Password'}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500"
                />
                {!isLogin && (
                  <input
                    type="text"
                    placeholder={currentLang === 'tr' ? 'Kullanıcı Adı' : 'Username'}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 text-white placeholder-gray-500"
                  />
                )}
                <button className="w-full px-4 py-2 bg-red-500 text-white hover:bg-red-600 transition-colors font-bold">
                  {isLogin ? t.login : t.register}
                </button>
              </div>
              
              <p className="text-center mt-4 text-gray-400 text-sm">
                {isLogin ? (currentLang === 'tr' ? 'Hesabınız yok mu? ' : "Don't have an account? ") : (currentLang === 'tr' ? 'Zaten hesabınız var mı? ' : 'Already have an account? ')}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-red-500 hover:text-red-400 cursor-pointer"
                >
                  {isLogin ? t.register : t.login}
                </button>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); opacity: 0; }
          50% { transform: translateY(-20px); opacity: 0.8; }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        body {
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
      `}</style>
    </>
  );
};

export default MinecraftModsSite;
